// Change a membership's end date (aamcirc/admin/routers/desk/edits.py).
// Dates are ISO days, YYYY-MM-DD, handled as plain numbers: no time zones.
import { etToday } from './activity'

/** Mirrors aamcirc/desk/common.py END_DATE_REASONS. */
export const REASONS = [
  { code: 'missed_issues', label: 'Missed or damaged issues' },
  { code: 'goodwill', label: 'Goodwill or complaint' },
  { code: 'data_correction', label: 'Correcting a data error' },
  { code: 'payment_elsewhere', label: 'Payment applied elsewhere' },
  { code: 'other', label: 'Other (say why in the note)' },
] as const

export type ReasonCode = (typeof REASONS)[number]['code']

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function parts(iso: string): [number, number, number] {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return [y, m, d]
}

const lastDay = (y: number, m: number): number => new Date(Date.UTC(y, m, 0)).getUTCDate()
const pad = (n: number): string => String(n).padStart(2, '0')

function landOn(y: number, m: number, day: number, monthEnd: boolean): string {
  const last = lastDay(y, m)
  return `${y}-${pad(m)}-${pad(monthEnd ? last : Math.min(day, last))}`
}

/** `end` moved by `n` months. The day clamps to the month's end, and a
 *  month-end date stays at month end (Feb 28 + 1 → Mar 31). */
export function addMonths(end: string, n: number): string {
  const [y, m, d] = parts(end)
  const idx = y * 12 + (m - 1) + n
  return landOn(Math.floor(idx / 12), (idx % 12) + 1, d, d === lastDay(y, m))
}

/** Whole calendar months from `a` to `b` (negative when `b` is earlier). */
export function monthsBetween(a: string, b: string): number {
  const [ay, am] = parts(a)
  const [by, bm] = parts(b)
  return (by - ay) * 12 + (bm - am)
}

/** The "Set to" month (YYYY-MM), keeping the base date's day the way addMonths does. */
export function setToMonth(base: string, ym: string): string {
  return addMonths(base, monthsBetween(base, `${ym}-01`))
}

export function canSave({ reason, note }: { reason: string; note: string }): boolean {
  if (!REASONS.some((r) => r.code === reason)) return false
  return reason !== 'other' || note.trim() !== ''
}

/** "May 31, 2027". */
export function fmtDate(iso: string): string {
  const [y, m, d] = parts(iso)
  return `${MONTHS[m - 1]} ${d}, ${y}`
}

/** "+2 months", "−1 month", "" for no change. */
export function deltaText(n: number): string {
  if (n === 0) return ''
  const abs = Math.abs(n)
  return `${n > 0 ? '+' : '−'}${abs} ${abs === 1 ? 'month' : 'months'}`
}

/** Where the "+n months" chips count from: the old end while it is today or
 *  later; today when it has passed (a lapsed membership) or there is none.
 *  So on a lapsed title "+2 months" means two months from today. */
export function startFrom(end: string | null, today: string): string {
  return end && end.slice(0, 10) >= today ? end.slice(0, 10) : today
}

/** The Desk route restores an expired membership whose new end is after today
 *  (aamcirc/admin/routers/desk/edits.py; one ending today would lapse tonight). */
export function restores({ status, newEnd, today }: { status: string | null | undefined; newEnd: string; today: string }): boolean {
  return status === 'expired' && newEnd > today
}

/** Today as YYYY-MM-DD in America/New_York, the calendar the backend's
 *  restore rule uses (edits.py), whatever zone the browser is in. */
export const todayIso = (now: Date = new Date()): string => etToday(now)
